import React, { useState, useEffect } from 'react';
import AdminLayout from '../../components/AdminLayout';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { Link, useNavigate } from 'react-router-dom';

const { FiSearch, FiMessageSquare, FiPhone, FiTrash2, FiCalendar, FiPlus, FiX, FiCheck } = FiIcons;

const Subscribers = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [subscribers, setSubscribers] = useState([]);
  const [notification, setNotification] = useState(null);
  const navigate = useNavigate();

  // New Subscriber Form State
  const [newSubscriber, setNewSubscriber] = useState({
    name: '',
    company: '',
    phone: '',
    fleetSize: '1-5',
    nextInspection: '',
    status: 'Active'
  });

  // Load Data
  useEffect(() => {
    const storedSubs = localStorage.getItem('ramirez_subscribers');
    if (storedSubs) {
      setSubscribers(JSON.parse(storedSubs));
    } else {
      const defaultSubs = [
        { id: 1, name: "Carlos Rivera", company: "Rivera Transport", phone: "9095550101", fleetSize: "6-20", nextInspection: "2024-04-15", status: "Active" },
        { id: 2, name: "Sarah Smith", company: "Smith Logistics", phone: "9095550123", fleetSize: "20+", nextInspection: "2024-03-28", status: "Due Soon" },
        { id: 3, name: "Mike Johnson", company: "MJ Trucking", phone: "9095550199", fleetSize: "1-5", nextInspection: "2024-05-10", status: "Active" },
        { id: 4, name: "David Chen", company: "Chen Distribution", phone: "6265550144", fleetSize: "1-5", nextInspection: "2024-04-02", status: "Active" },
        { id: 5, name: "Elena Rodriguez", company: "ER Hauling", phone: "9515550177", fleetSize: "6-20", nextInspection: "2024-03-20", status: "Urgent" },
      ];
      setSubscribers(defaultSubs);
      localStorage.setItem('ramirez_subscribers', JSON.stringify(defaultSubs));
    }
  }, []);

  // Save Data
  useEffect(() => {
    if (subscribers.length >= 0) {
      localStorage.setItem('ramirez_subscribers', JSON.stringify(subscribers));
    }
  }, [subscribers]);

  const showNotification = (msg) => {
    setNotification(msg);
    setTimeout(() => setNotification(null), 3000);
  };

  const handleAddSubscriber = (e) => {
    e.preventDefault();
    const id = Date.now(); // Unique ID
    const subscriber = { ...newSubscriber, id };
    setSubscribers([subscriber, ...subscribers]);
    setIsAddModalOpen(false);
    setNewSubscriber({ name: '', company: '', phone: '', fleetSize: '1-5', nextInspection: '', status: 'Active' });
    showNotification("Subscriber added successfully!");
  };

  const handleDelete = (id) => {
    if (window.confirm("Are you sure you want to remove this subscriber?")) {
      setSubscribers(subscribers.filter(sub => sub.id !== id));
      showNotification("Subscriber removed.");
    }
  };

  const handleSMS = (phone) => {
    // Navigate to SMS campaign with pre-filled number if we implemented context, 
    // for now just go to the page
    navigate('/admin/sms');
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Active': return 'bg-green-900/30 text-green-400 border-green-800';
      case 'Due Soon': return 'bg-yellow-900/30 text-yellow-400 border-yellow-800';
      case 'Urgent': return 'bg-red-900/30 text-red-400 border-red-800';
      default: return 'bg-gray-700 text-gray-300';
    }
  };

  const filteredSubscribers = subscribers.filter(sub =>
    sub.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    sub.company.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <AdminLayout>
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white mb-2">Reminder Subscribers</h1>
          <p className="text-gray-400">Manage fleet owners registered for automated alerts.</p>
        </div>
        <div className="flex gap-2 w-full md:w-auto">
          <div className="relative flex-1 md:w-64">
            <SafeIcon icon={FiSearch} className="absolute left-3 top-3 text-gray-500" />
            <input
              type="text"
              placeholder="Search subscribers..."
              className="w-full bg-gray-800 border border-gray-700 text-white rounded-lg pl-10 pr-4 py-2.5 focus:ring-2 focus:ring-blue-500 outline-none"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <button
            onClick={() => setIsAddModalOpen(true)}
            className="bg-green-600 text-white px-4 py-2.5 rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2 whitespace-nowrap shadow-lg shadow-green-900/20"
          >
            <SafeIcon icon={FiPlus} />
            <span>Add New</span>
          </button>
          <Link to="/admin/sms" className="bg-blue-600 text-white px-4 py-2.5 rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2 whitespace-nowrap">
            <SafeIcon icon={FiMessageSquare} />
            <span>Blast SMS</span>
          </Link>
        </div>
      </div>

      {notification && (
        <div className="fixed top-20 right-4 bg-green-600 text-white px-4 py-2 rounded-lg shadow-lg z-50 flex items-center gap-2 animate-fade-in-up">
          <SafeIcon icon={FiCheck} />
          {notification}
        </div>
      )}

      <div className="bg-gray-800 border border-gray-700 rounded-xl overflow-hidden shadow-xl">
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead>
              <tr className="bg-gray-900/50 border-b border-gray-700 text-gray-400 text-sm uppercase">
                <th className="px-6 py-4 font-medium">Subscriber</th>
                <th className="px-6 py-4 font-medium">Contact</th>
                <th className="px-6 py-4 font-medium">Fleet Size</th>
                <th className="px-6 py-4 font-medium">Next Inspection</th>
                <th className="px-6 py-4 font-medium">Status</th>
                <th className="px-6 py-4 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-700">
              {filteredSubscribers.length > 0 ? (
                filteredSubscribers.map((sub) => (
                  <tr key={sub.id} className="hover:bg-gray-700/30 transition-colors group">
                    <td className="px-6 py-4">
                      <div className="font-bold text-white">{sub.company}</div>
                      <div className="text-sm text-gray-400">{sub.name}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2 text-gray-300">
                        <SafeIcon icon={FiPhone} className="text-gray-500" />
                        {sub.phone}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-gray-300">
                      {sub.fleetSize} Trucks
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2 text-gray-300">
                        <SafeIcon icon={FiCalendar} className="text-gray-500" />
                        {sub.nextInspection}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getStatusColor(sub.status)}`}>
                        {sub.status}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button onClick={() => handleSMS(sub.phone)} className="p-2 bg-blue-600/20 text-blue-400 rounded hover:bg-blue-600 hover:text-white transition-colors" title="Send SMS">
                          <SafeIcon icon={FiMessageSquare} />
                        </button>
                        <button onClick={() => handleDelete(sub.id)} className="p-2 bg-red-600/20 text-red-400 rounded hover:bg-red-600 hover:text-white transition-colors" title="Remove">
                          <SafeIcon icon={FiTrash2} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="6" className="px-6 py-12 text-center text-gray-500">
                    No subscribers found. Add one to get started.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="bg-gray-900/30 px-6 py-4 border-t border-gray-700 flex justify-between items-center text-sm text-gray-400">
          <span>Showing {filteredSubscribers.length} subscribers</span>
        </div>
      </div>

      {/* Add Subscriber Modal */}
      {isAddModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
          <div className="bg-gray-800 rounded-2xl shadow-2xl w-full max-w-lg border border-gray-700 overflow-hidden animate-fade-in-up">
            <div className="p-6 border-b border-gray-700 flex justify-between items-center bg-gray-900/50">
              <h2 className="text-xl font-bold text-white flex items-center gap-2">
                <SafeIcon icon={FiPlus} className="text-green-500" />
                Set Up New Reminder
              </h2>
              <button onClick={() => setIsAddModalOpen(false)} className="text-gray-400 hover:text-white transition-colors" type="button">
                <SafeIcon icon={FiX} className="text-xl" />
              </button>
            </div>
            <form onSubmit={handleAddSubscriber} className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-gray-400 text-sm font-medium mb-1">Company Name</label>
                  <input type="text" required value={newSubscriber.company} onChange={(e) => setNewSubscriber({ ...newSubscriber, company: e.target.value })} placeholder="e.g. JD Trucking" className="w-full bg-gray-700 border border-gray-600 text-white rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 outline-none" />
                </div>
                <div>
                  <label className="block text-gray-400 text-sm font-medium mb-1">Contact Name</label>
                  <input type="text" required value={newSubscriber.name} onChange={(e) => setNewSubscriber({ ...newSubscriber, name: e.target.value })} placeholder="e.g. John Doe" className="w-full bg-gray-700 border border-gray-600 text-white rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 outline-none" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-gray-400 text-sm font-medium mb-1">Phone Number</label>
                  <input type="tel" required value={newSubscriber.phone} onChange={(e) => setNewSubscriber({ ...newSubscriber, phone: e.target.value })} placeholder="(909) 555-0123" className="w-full bg-gray-700 border border-gray-600 text-white rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 outline-none" />
                </div>
                <div>
                  <label className="block text-gray-400 text-sm font-medium mb-1">Fleet Size</label>
                  <select value={newSubscriber.fleetSize} onChange={(e) => setNewSubscriber({ ...newSubscriber, fleetSize: e.target.value })} className="w-full bg-gray-700 border border-gray-600 text-white rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 outline-none">
                    <option value="1-5">1-5 Trucks</option>
                    <option value="6-20">6-20 Trucks</option>
                    <option value="20+">20+ Trucks</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-gray-400 text-sm font-medium mb-1">Next Inspection Date</label>
                <input type="date" required value={newSubscriber.nextInspection} onChange={(e) => setNewSubscriber({ ...newSubscriber, nextInspection: e.target.value })} className="w-full bg-gray-700 border border-gray-600 text-white rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 outline-none" />
              </div>
              <div className="pt-4 flex gap-3">
                <button type="button" onClick={() => setIsAddModalOpen(false)} className="flex-1 px-4 py-2 bg-gray-700 text-gray-300 rounded-lg hover:bg-gray-600 transition-colors font-medium">
                  Cancel
                </button>
                <button type="submit" className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium flex items-center justify-center gap-2">
                  <SafeIcon icon={FiCheck} />
                  Add Subscriber
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default Subscribers;